import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import axiosInstance from "../../../api/axios";
import { Button, Card, Form, Pagination, Spinner, Table } from "react-bootstrap";
import {
  ArrowCounterclockwise,
  ArrowLeft,
  Calendar3,
  ChatLeftText,
  ChevronDown,
  ExclamationCircle,
  Search,
  Shop,
} from "react-bootstrap-icons";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../../layouts/Header";
import Footer from "../../../layouts/Footer";
import FeedbackDetailDrawer from "./FeedbackDetailDrawer";
import { RatingPill, SourceBadge } from "./FeedbackListing";

const API_BASE_URL = "https://apisay.xpansecoffee.com/api";

const RANGE_OPTIONS = [
  "Today",
  "Yesterday",
  "Last 7 Days",
  "Last 30 Days",
  "Last 90 Days",
  "All Time",
  "Custom Range",
];

const PER_PAGE_OPTIONS = [10, 20, 50, 100];

const DEFAULT_FILTERS = {
  outletId: "all",
  dateRange: "Last 30 Days",
  feedbackType: "all",
  hasSuggestions: "all",
};

const pad = (value) => String(value).padStart(2, "0");

const formatDateForApi = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const getPresetDates = (range) => {
  if (range === "All Time") return {};

  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  switch (range) {
    case "Today":
      break;
    case "Yesterday":
      start.setDate(start.getDate() - 1);
      end.setDate(end.getDate() - 1);
      break;
    case "Last 7 Days":
      start.setDate(start.getDate() - 6);
      break;
    case "Last 90 Days":
      start.setDate(start.getDate() - 89);
      break;
    case "Last 30 Days":
    default:
      start.setDate(start.getDate() - 29);
      break;
  }

  return {
    from_date: formatDateForApi(start),
    to_date: formatDateForApi(end),
  };
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const normalizedValue = String(value).includes("T")
    ? value
    : String(value).replace(" ", "T");

  const date = new Date(normalizedValue);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getFeedbackName = (feedback) =>
  feedback?.user?.name || feedback?.user_name || feedback?.customer_name || "Guest";

const getMobile = (feedback) =>
  feedback?.user?.mobile || feedback?.user_mobile || feedback?.mobile || "";

const getOutletName = (feedback) =>
  feedback?.outlet?.name || feedback?.outlet_name || "Unknown Outlet";

const getSource = (feedback) =>
  feedback?.source || (feedback?.order_id ? "order" : "walk-in");

const FeedbackCustomersList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const filterRef = useRef(null);
  const requestIdRef = useRef(0);

  const initialFilters = { ...DEFAULT_FILTERS, ...(location.state || {}) };

  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [, setSkin] = useState(currentSkin);

  const accessToken = localStorage.getItem("authToken");

  /* ---------------------------------------------------------------------- */
  /* FILTERS                                                                */
  /* ---------------------------------------------------------------------- */

  const [outlets, setOutlets] = useState([{ value: "all", label: "All Outlets" }]);
  const [outletSearch, setOutletSearch] = useState("");
  const [selectedOutletId, setSelectedOutletId] = useState(initialFilters.outletId);
  const [showOutletMenu, setShowOutletMenu] = useState(false);

  const [dateRange, setDateRange] = useState(initialFilters.dateRange);
  const [showRangeMenu, setShowRangeMenu] = useState(false);

  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [appliedCustomRange, setAppliedCustomRange] = useState(null);
  const [customRangeError, setCustomRangeError] = useState("");

  const [feedbackType, setFeedbackType] = useState(initialFilters.feedbackType);
  const [hasSuggestions, setHasSuggestions] = useState(initialFilters.hasSuggestions);

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  /* ---------------------------------------------------------------------- */
  /* DATA                                                                   */
  /* ---------------------------------------------------------------------- */

  const [feedbacks, setFeedbacks] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 1,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showFeedbackDrawer, setShowFeedbackDrawer] = useState(false);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);

  const getHeaders = () =>
    accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

  const getDateParams = () => {
    if (dateRange === "Custom Range") {
      return appliedCustomRange
        ? { from_date: appliedCustomRange.from, to_date: appliedCustomRange.to }
        : {};
    }

    return getPresetDates(dateRange);
  };

  /* ---------------------------------------------------------------------- */
  /* API                                                                    */
  /* ---------------------------------------------------------------------- */

  const getOutlets = async () => {
    try {
      const response = await axiosInstance.get("https://api.xpansecafe.com/outlet", {
        headers: getHeaders(),
      });

      if (response?.data?.status === true) {
        const outletData = response?.data?.data?.outlets || [];

        setOutlets([
          { value: "all", label: "All Outlets" },
          ...outletData.map((outlet) => ({
            value: outlet._id,
            label: outlet.name || outlet.outletName || "Unknown Outlet",
          })),
        ]);
      }
    } catch (err) {
      console.error("Failed to fetch outlets:", err);
    }
  };

  const fetchFeedbacks = async () => {
    const requestId = ++requestIdRef.current;

    try {
      setLoading(true);
      setError("");

      const params = {
        ...getDateParams(),
        page,
        per_page: perPage,
      };

      if (selectedOutletId !== "all") params.outlet_id = selectedOutletId;
      if (feedbackType === "order") params.has_order = true;
      if (feedbackType === "walk-in") params.has_order = false;
      if (hasSuggestions !== "all") params.has_suggestions = hasSuggestions === "yes";

      const term = searchTerm.trim();
      if (term) {
        // Mobile is an exact-match filter on the API, name is partial.
        if (/^\+?\d{8,15}$/.test(term)) {
          params.mobile = term;
        } else {
          params.user_name = term;
        }
      }

      const response = await axiosInstance.get(`${API_BASE_URL}/feedbacks`, {
        params,
        headers: getHeaders(),
      });

      if (requestId !== requestIdRef.current) return;

      if (response?.data?.status === true) {
        const data = response?.data?.data || {};

        setFeedbacks(data.items || []);
        setPagination(
          data.pagination || { page: 1, per_page: perPage, total: 0, total_pages: 1 },
        );
      } else {
        setFeedbacks([]);
        setError(response?.data?.error || "Unable to load feedback.");
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return;

      console.error("Feedback fetch error:", err);
      setFeedbacks([]);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Unable to load feedback.",
      );
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* EFFECTS                                                                */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    getOutlets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filterKey = JSON.stringify([
    selectedOutletId,
    dateRange,
    appliedCustomRange,
    feedbackType,
    hasSuggestions,
    searchTerm,
    perPage,
  ]);
  const lastFilterKeyRef = useRef(filterKey);

  useEffect(() => {
    // Wait for the user to apply a custom range before refetching.
    if (dateRange === "Custom Range" && !appliedCustomRange) return;

    if (lastFilterKeyRef.current !== filterKey) {
      lastFilterKeyRef.current = filterKey;

      if (page !== 1) {
        setPage(1);
        return;
      }
    }

    fetchFeedbacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, page]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowOutletMenu(false);
        setShowRangeMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------------------------------------------------------------------- */
  /* HANDLERS                                                               */
  /* ---------------------------------------------------------------------- */

  const selectRange = (option) => {
    setDateRange(option);
    setShowRangeMenu(false);
    setCustomRangeError("");

    if (option !== "Custom Range") {
      setAppliedCustomRange(null);
    }
  };

  const applyCustomRange = () => {
    if (!customFrom || !customTo) {
      setCustomRangeError("Select both From and To dates.");
      return;
    }

    if (customFrom > customTo) {
      setCustomRangeError("From date cannot be after To date.");
      return;
    }

    setCustomRangeError("");
    setAppliedCustomRange({ from: customFrom, to: customTo });
  };

  const resetFilters = () => {
    setSelectedOutletId(DEFAULT_FILTERS.outletId);
    setDateRange(DEFAULT_FILTERS.dateRange);
    setFeedbackType(DEFAULT_FILTERS.feedbackType);
    setHasSuggestions(DEFAULT_FILTERS.hasSuggestions);
    setCustomFrom("");
    setCustomTo("");
    setAppliedCustomRange(null);
    setCustomRangeError("");
    setSearchInput("");
    setSearchTerm("");
  };

  const openFeedbackDetail = (feedback) => {
    const feedbackId = feedback?.id || feedback?._id;
    if (!feedbackId) return;

    setSelectedFeedbackId(feedbackId);
    setShowFeedbackDrawer(true);
  };

  const closeFeedbackDrawer = () => {
    setShowFeedbackDrawer(false);
    setTimeout(() => setSelectedFeedbackId(null), 300);
  };

  /* ---------------------------------------------------------------------- */
  /* DERIVED                                                                */
  /* ---------------------------------------------------------------------- */

  const selectedOutletLabel =
    outlets.find((outlet) => outlet.value === selectedOutletId)?.label ||
    "All Outlets";

  const filteredOutlets = outlets.filter((outlet) =>
    outlet.label.toLowerCase().includes(outletSearch.trim().toLowerCase()),
  );

  const dateButtonLabel =
    dateRange === "Custom Range" && appliedCustomRange
      ? `${appliedCustomRange.from} → ${appliedCustomRange.to}`
      : dateRange;

  const totalPages = Math.max(1, Number(pagination?.total_pages || 1));
  const total = Number(pagination?.total || 0);
  const rangeStart = total ? (page - 1) * perPage + 1 : 0;
  const rangeEnd = Math.min(page * perPage, total);

  const awaitingCustomRange = dateRange === "Custom Range" && !appliedCustomRange;

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + 4);

    if (end - start < 4) start = Math.max(1, end - 4);

    const items = [];

    if (start > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => setPage(1)}>
          1
        </Pagination.Item>,
      );
      if (start > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
    }

    for (let currentPage = start; currentPage <= end; currentPage++) {
      items.push(
        <Pagination.Item
          key={currentPage}
          active={currentPage === page}
          onClick={() => setPage(currentPage)}
        >
          {currentPage}
        </Pagination.Item>,
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => setPage(totalPages)}>
          {totalPages}
        </Pagination.Item>,
      );
    }

    return (
      <Pagination className="fcl-pagination mb-0">
        <Pagination.Prev
          disabled={page <= 1}
          onClick={() => setPage((current) => Math.max(1, current - 1))}
        />
        {items}
        <Pagination.Next
          disabled={page >= totalPages}
          onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
        />
      </Pagination>
    );
  };

  return (
    <>
      <Header onSkin={setSkin} />

      <div className="main main-app fcl-page">
        <div className="fcl-container">
          <div className="fcl-header">
            <div>
              <Button
                variant="light"
                className="fcl-back-button"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft size={17} />
                Go Back
              </Button>

              <h1 className="fcl-title">
                Customer <span>Feedback</span>
              </h1>

              <p className="fcl-subtitle">
                Browse every feedback response by date range and outlet.
              </p>
            </div>
          </div>

          {/* FILTERS */}

          <Card className="fcl-card mb-3">
            <Card.Body className="fcl-filters" ref={filterRef}>
              <div className="fcl-dropdown">
                <button
                  type="button"
                  className={`fcl-filter-button ${showOutletMenu ? "active" : ""}`}
                  onClick={() => {
                    setShowOutletMenu(!showOutletMenu);
                    setShowRangeMenu(false);
                  }}
                >
                  <span>
                    <Shop size={15} />
                    {selectedOutletLabel}
                  </span>
                  <ChevronDown size={16} />
                </button>

                {showOutletMenu && (
                  <div className="fcl-dropdown-menu">
                    <Form.Control
                      size="sm"
                      autoFocus
                      className="fcl-dropdown-search"
                      placeholder="Search outlet..."
                      value={outletSearch}
                      onChange={(event) => setOutletSearch(event.target.value)}
                    />

                    {filteredOutlets.length === 0 ? (
                      <div className="fcl-dropdown-empty">No outlets found</div>
                    ) : (
                      filteredOutlets.map((outlet) => (
                        <button
                          type="button"
                          key={outlet.value}
                          className={`fcl-dropdown-item ${
                            selectedOutletId === outlet.value ? "selected" : ""
                          }`}
                          onClick={() => {
                            setSelectedOutletId(outlet.value);
                            setShowOutletMenu(false);
                            setOutletSearch("");
                          }}
                        >
                          {outlet.label}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              <div className="fcl-dropdown">
                <button
                  type="button"
                  className={`fcl-filter-button ${showRangeMenu ? "active" : ""}`}
                  onClick={() => {
                    setShowRangeMenu(!showRangeMenu);
                    setShowOutletMenu(false);
                  }}
                >
                  <span>
                    <Calendar3 size={14} />
                    {dateButtonLabel}
                  </span>
                  <ChevronDown size={16} />
                </button>

                {showRangeMenu && (
                  <div className="fcl-dropdown-menu">
                    {RANGE_OPTIONS.map((option) => (
                      <button
                        type="button"
                        key={option}
                        className={`fcl-dropdown-item ${
                          dateRange === option ? "selected" : ""
                        }`}
                        onClick={() => selectRange(option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Form.Select
                className="fcl-select"
                value={feedbackType}
                onChange={(event) => setFeedbackType(event.target.value)}
              >
                <option value="all">All feedback</option>
                <option value="order">Order feedback</option>
                <option value="walk-in">Walk-in feedback</option>
              </Form.Select>

              <Form.Select
                className="fcl-select"
                value={hasSuggestions}
                onChange={(event) => setHasSuggestions(event.target.value)}
              >
                <option value="all">All suggestions</option>
                <option value="yes">Has suggestion</option>
                <option value="no">No suggestion</option>
              </Form.Select>

              <div className="fcl-search">
                <Search size={13} />
                <Form.Control
                  placeholder="Customer name or mobile"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </div>

              <button type="button" className="fcl-reset-button" onClick={resetFilters}>
                <ArrowCounterclockwise size={13} />
                Reset
              </button>

              {dateRange === "Custom Range" && (
                <div className="fcl-custom-range">
                  <Form.Group>
                    <Form.Label>From</Form.Label>
                    <Form.Control
                      type="date"
                      value={customFrom}
                      max={customTo || formatDateForApi(new Date())}
                      onChange={(event) => setCustomFrom(event.target.value)}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>To</Form.Label>
                    <Form.Control
                      type="date"
                      value={customTo}
                      min={customFrom || undefined}
                      max={formatDateForApi(new Date())}
                      onChange={(event) => setCustomTo(event.target.value)}
                    />
                  </Form.Group>

                  <Button className="fcl-apply-button" onClick={applyCustomRange}>
                    Apply
                  </Button>

                  {customRangeError && (
                    <span className="fcl-range-error">{customRangeError}</span>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>

          {error && (
            <div className="fcl-error">
              <ExclamationCircle size={18} />
              {error}
            </div>
          )}

          {/* TABLE */}

          <Card className="fcl-card overflow-hidden">
            <Card.Body className="p-0">
              {awaitingCustomRange ? (
                <div className="fcl-empty">
                  <Calendar3 size={28} />
                  <strong>Select a date range</strong>
                  <span>Pick From and To dates, then click Apply.</span>
                </div>
              ) : loading ? (
                <div className="fcl-loading">
                  <Spinner animation="border" size="sm" />
                  Loading feedback...
                </div>
              ) : feedbacks.length === 0 ? (
                <div className="fcl-empty">
                  <ChatLeftText size={30} />
                  <strong>No feedback found</strong>
                  <span>Try changing the outlet, date range or other filters.</span>
                </div>
              ) : (
                <>
                  <div className="fcl-desktop-table">
                    <Table responsive borderless hover className="fcl-table mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Customer</th>
                          <th>Outlet</th>
                          <th>Experience</th>
                          <th>Recommend</th>
                          <th>Suggestion</th>
                          <th>Source</th>
                          <th>Submitted</th>
                          <th></th>
                        </tr>
                      </thead>

                      <tbody>
                        {feedbacks.map((feedback, index) => (
                          <tr
                            key={feedback.id || feedback._id || index}
                            onClick={() => openFeedbackDetail(feedback)}
                          >
                            <td className="fcl-index">{rangeStart + index}</td>

                            <td>
                              <div className="fcl-customer">
                                <div className="fcl-avatar">
                                  {getFeedbackName(feedback).charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <strong>{getFeedbackName(feedback)}</strong>
                                  <span>{getMobile(feedback)}</span>
                                </div>
                              </div>
                            </td>

                            <td>
                              <span className="fcl-outlet-name">
                                {getOutletName(feedback)}
                              </span>
                            </td>

                            <td>
                              <RatingPill value={feedback?.overall_experience} />
                            </td>

                            <td>
                              <RatingPill value={feedback?.recommend_rating} />
                            </td>

                            <td>
                              <div className="fcl-suggestion">
                                {feedback?.suggestions || "—"}
                              </div>
                            </td>

                            <td>
                              <SourceBadge source={getSource(feedback)} />
                            </td>

                            <td>
                              <span className="fcl-date">
                                {formatDateTime(feedback.submitted_at)}
                              </span>
                            </td>

                            <td>
                              <button
                                type="button"
                                className="fcl-view-button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openFeedbackDetail(feedback);
                                }}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <div className="fcl-mobile-list">
                    {feedbacks.map((feedback, index) => (
                      <div
                        key={feedback.id || feedback._id || index}
                        className="fcl-mobile-item"
                        onClick={() => openFeedbackDetail(feedback)}
                      >
                        <div className="fcl-mobile-top">
                          <div className="fcl-customer">
                            <div className="fcl-avatar">
                              {getFeedbackName(feedback).charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <strong>{getFeedbackName(feedback)}</strong>
                              <span>{getOutletName(feedback)}</span>
                            </div>
                          </div>
                          <RatingPill value={feedback?.overall_experience} />
                        </div>

                        {feedback?.suggestions && (
                          <p className="fcl-mobile-suggestion">
                            “{feedback.suggestions}”
                          </p>
                        )}

                        <div className="fcl-mobile-footer">
                          <SourceBadge source={getSource(feedback)} />
                          <span>{formatDateTime(feedback.submitted_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {!awaitingCustomRange && total > 0 && (
                <div className="fcl-footer">
                  <div className="fcl-footer-info">
                    <span>
                      Showing <strong>{rangeStart}</strong>–<strong>{rangeEnd}</strong>{" "}
                      of <strong>{total}</strong> responses
                    </span>

                    <Form.Select
                      size="sm"
                      className="fcl-per-page"
                      value={perPage}
                      onChange={(event) => setPerPage(Number(event.target.value))}
                    >
                      {PER_PAGE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option} / page
                        </option>
                      ))}
                    </Form.Select>
                  </div>

                  {renderPagination()}
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      <div className="mt-4">
        <Footer />
      </div>

      <FeedbackDetailDrawer
        show={showFeedbackDrawer}
        onHide={closeFeedbackDrawer}
        feedbackId={selectedFeedbackId}
        accessToken={accessToken}
        apiBaseUrl={API_BASE_URL}
      />

      <style>
        {`
                .fcl-page {
                    background: #F7FBFF;
                    min-height: 100vh;
                }

                .fcl-container {
                    padding: 26px 28px 42px;
                }

                .fcl-header {
                    margin-bottom: 20px;
                }

                .fcl-back-button {
                    border: 1px solid #D6E6F5 !important;
                    border-radius: 11px !important;
                    display: inline-flex !important;
                    align-items: center;
                    gap: 7px;
                    font-size: 13px !important;
                    font-weight: 700 !important;
                    margin-bottom: 18px;
                    background: #ffffff !important;
                    color: #102A43 !important;
                }

                .fcl-title {
                    color: #071C3A;
                    font-size: 34px;
                    line-height: 1.15;
                    font-weight: 700;
                    margin: 0 0 7px;
                }

                .fcl-title span {
                    color: #1677E8;
                }

                .fcl-subtitle {
                    color: #60748A;
                    font-size: 14px;
                    margin: 0;
                }

                .fcl-card {
                    border: 1px solid #DDEEFF !important;
                    border-radius: 16px !important;
                    box-shadow: none !important;
                }

                .fcl-filters {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 10px;
                    padding: 14px 16px !important;
                }

                .fcl-dropdown {
                    position: relative;
                }

                .fcl-filter-button {
                    min-width: 170px;
                    max-width: 260px;
                    height: 38px;
                    padding: 0 12px;
                    border: 1px solid #D6E6F5;
                    border-radius: 10px;
                    background: #ffffff;
                    color: #102A43;
                    font-size: 12px;
                    font-weight: 700;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 14px;
                }

                .fcl-filter-button > span {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                }

                .fcl-filter-button.active {
                    border-color: #4C9BE8;
                    box-shadow: 0 0 0 2px rgba(22,119,232,.1);
                }

                .fcl-dropdown-menu {
                    position: absolute;
                    left: 0;
                    top: calc(100% + 7px);
                    width: 240px;
                    max-height: 320px;
                    overflow-y: auto;
                    padding: 6px;
                    background: #ffffff;
                    border: 1px solid #DDEEFF;
                    border-radius: 12px;
                    box-shadow: 0 16px 40px rgba(37,112,180,.14);
                    z-index: 1100;
                }

                .fcl-dropdown-search {
                    margin-bottom: 6px;
                    border-radius: 8px;
                    font-size: 12px;
                    box-shadow: none !important;
                }

                .fcl-dropdown-empty {
                    color: #71869A;
                    font-size: 12px;
                    padding: 9px 11px;
                }

                .fcl-dropdown-item {
                    width: 100%;
                    border: 0;
                    background: transparent;
                    text-align: left;
                    padding: 9px 11px;
                    border-radius: 8px;
                    color: #102A43;
                    font-size: 13px;
                    font-weight: 600;
                }

                .fcl-dropdown-item:hover,
                .fcl-dropdown-item.selected {
                    background: #EDF7FF;
                    color: #1677E8;
                }

                .fcl-select {
                    width: auto !important;
                    min-width: 150px;
                    height: 38px;
                    border-color: #D6E6F5 !important;
                    border-radius: 10px !important;
                    color: #334E68 !important;
                    font-size: 12px !important;
                    font-weight: 600;
                    box-shadow: none !important;
                }

                .fcl-search {
                    position: relative;
                    flex: 1;
                    min-width: 200px;
                }

                .fcl-search svg {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #71869A;
                }

                .fcl-search .form-control {
                    height: 38px;
                    padding-left: 32px;
                    border-color: #D6E6F5;
                    border-radius: 10px;
                    font-size: 12px;
                    box-shadow: none;
                }

                .fcl-reset-button {
                    height: 38px;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    border: 1px solid #D6E6F5;
                    border-radius: 10px;
                    background: #ffffff;
                    color: #486581;
                    padding: 0 12px;
                    font-size: 12px;
                    font-weight: 700;
                }

                .fcl-reset-button:hover {
                    color: #1677E8;
                    border-color: #C9E4FF;
                }

                .fcl-custom-range {
                    width: 100%;
                    display: flex;
                    align-items: flex-end;
                    flex-wrap: wrap;
                    gap: 10px;
                    padding-top: 12px;
                    border-top: 1px dashed #DDEEFF;
                }

                .fcl-custom-range .form-label {
                    color: #60748A;
                    font-size: 10px;
                    text-transform: uppercase;
                    letter-spacing: .4px;
                    font-weight: 800;
                    margin-bottom: 4px;
                }

                .fcl-custom-range .form-control {
                    height: 38px;
                    min-width: 170px;
                    border-color: #D6E6F5;
                    border-radius: 10px;
                    font-size: 12px;
                    box-shadow: none;
                }

                .fcl-apply-button {
                    height: 38px;
                    border: 0 !important;
                    border-radius: 10px !important;
                    background: #1677E8 !important;
                    padding: 0 18px !important;
                    font-size: 12px !important;
                    font-weight: 700 !important;
                }

                .fcl-range-error {
                    color: #F25B2A;
                    font-size: 12px;
                    font-weight: 600;
                    align-self: center;
                }

                .fcl-error {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    background: #FFF1EA;
                    color: #F25B2A;
                    border: 1px solid #FFE0D2;
                    border-radius: 12px;
                    padding: 12px 15px;
                    margin-bottom: 14px;
                    font-size: 13px;
                    font-weight: 700;
                }

                .fcl-table {
                    vertical-align: middle;
                }

                .fcl-table thead th {
                    background: #F4FAFF;
                    color: #60748A;
                    border-bottom: 1px solid #EAF2FB;
                    padding: 13px 15px;
                    font-size: 10px;
                    text-transform: uppercase;
                    letter-spacing: .4px;
                    font-weight: 800;
                    white-space: nowrap;
                }

                .fcl-table tbody tr {
                    cursor: pointer;
                    border-bottom: 1px solid #EAF2FB;
                    transition: background .15s ease;
                }

                .fcl-table tbody tr:hover {
                    background: #F7FBFF;
                }

                .fcl-table tbody td {
                    padding: 13px 15px;
                    color: #334E68;
                    font-size: 12px;
                }

                .fcl-index {
                    color: #8AA0B5 !important;
                    font-weight: 700;
                }

                .fcl-customer {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                }

                .fcl-avatar {
                    width: 32px;
                    height: 32px;
                    flex: 0 0 32px;
                    border-radius: 50%;
                    background: #DCEEFF;
                    color: #1677E8;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: 800;
                }

                .fcl-customer strong {
                    display: block;
                    color: #102A43;
                    font-size: 12px;
                    white-space: nowrap;
                }

                .fcl-customer span {
                    display: block;
                    color: #8AA0B5;
                    font-size: 10px;
                    margin-top: 1px;
                }

                .fcl-outlet-name {
                    color: #334E68;
                    font-weight: 600;
                    white-space: nowrap;
                }

                .fcl-page .rating-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    border-radius: 7px;
                    padding: 5px 7px;
                    font-size: 11px;
                    font-weight: 800;
                }

                .fcl-page .rating-pill svg {
                    color: #FFB51B;
                }

                .fcl-page .source-badge {
                    display: inline-flex;
                    padding: 5px 8px;
                    border-radius: 7px;
                    background: #EDF7FF;
                    color: #1677E8;
                    font-size: 9px;
                    text-transform: capitalize;
                    font-weight: 800;
                }

                .fcl-page .source-badge.walk-in {
                    background: #EAFBF4;
                    color: #08A878;
                }

                .fcl-suggestion {
                    max-width: 260px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    color: #60748A;
                }

                .fcl-date {
                    white-space: nowrap;
                    color: #60748A;
                    font-size: 11px;
                }

                .fcl-view-button {
                    border: 0;
                    border-radius: 7px;
                    background: #EDF7FF;
                    color: #1677E8;
                    padding: 6px 10px;
                    font-size: 10px;
                    font-weight: 800;
                }

                .fcl-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 12px;
                    padding: 13px 16px;
                    border-top: 1px solid #EAF2FB;
                    background: #ffffff;
                    color: #71869A;
                    font-size: 11px;
                }

                .fcl-footer-info {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .fcl-per-page {
                    width: auto !important;
                    border-color: #D6E6F5 !important;
                    border-radius: 8px !important;
                    font-size: 11px !important;
                    box-shadow: none !important;
                }

                .fcl-pagination .page-link {
                    border: 0;
                    border-radius: 7px !important;
                    color: #486581;
                    font-size: 11px;
                    margin: 0 2px;
                    min-width: 29px;
                    text-align: center;
                    box-shadow: none !important;
                }

                .fcl-pagination .page-item.active .page-link {
                    background: #1677E8;
                    color: #ffffff;
                }

                .fcl-loading {
                    min-height: 220px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 9px;
                    color: #60748A;
                    font-size: 13px;
                    font-weight: 600;
                }

                .fcl-empty {
                    min-height: 240px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 7px;
                    color: #71869A;
                    font-size: 12px;
                    text-align: center;
                }

                .fcl-empty strong {
                    color: #334E68;
                    font-size: 14px;
                }

                .fcl-mobile-list {
                    display: none;
                }

                @media (max-width: 767px) {
                    .fcl-container {
                        padding: 18px 14px 30px;
                    }

                    .fcl-title {
                        font-size: 27px;
                    }

                    .fcl-filters > * {
                        width: 100%;
                        max-width: none;
                    }

                    .fcl-filter-button,
                    .fcl-select {
                        width: 100% !important;
                        max-width: none;
                    }

                    .fcl-dropdown-menu {
                        width: 100%;
                    }

                    .fcl-custom-range .form-group {
                        flex: 1;
                    }

                    .fcl-custom-range .form-control {
                        min-width: 0;
                    }

                    .fcl-desktop-table {
                        display: none;
                    }

                    .fcl-mobile-list {
                        display: block;
                    }

                    .fcl-mobile-item {
                        padding: 15px;
                        border-bottom: 1px solid #EAF2FB;
                        cursor: pointer;
                    }

                    .fcl-mobile-top {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        gap: 10px;
                    }

                    .fcl-mobile-suggestion {
                        color: #486581;
                        font-size: 11px;
                        line-height: 1.55;
                        margin: 12px 0;
                        padding: 9px 11px;
                        border-radius: 8px;
                        background: #F7FBFF;
                    }

                    .fcl-mobile-footer {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        color: #71869A;
                        font-size: 10px;
                        margin-top: 11px;
                    }

                    .fcl-footer {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                }
                `}
      </style>
    </>
  );
};

export default FeedbackCustomersList;
